<template>
    <li class="nav-item dropdown" ref="root">
        <a class="navbar-nav-link dropdown-toggle caret-0" @click="show = !show">
            <i class="icon-bubbles4"></i>
            <span class="d-md-none ml-2">{{ translate('notifications.messages') }}</span>
            <span class="badge ml-auto ml-md-0" :class="[unreadNotifications ? 'badge-mark border-orange-300' : '']"></span>
        </a>

        <div class="dropdown-menu dropdown-menu-right dropdown-content wmin-md-350" :class="{'show': show}">
            <div class="dropdown-content-header">
                <span class="font-weight-semibold">{{ translate('notifications.messages') }}</span>
            </div>
            <div class="dropdown-content-body dropdown-scrollable">
                <ul class="media-list" v-if="notifications.length > 0">
                    <li class="media"
                        v-for="notification in notifications"
                    >
                        <div class="media-body">
                            <div class="media-title">
                                <span
                                class="font-weight-semibold"
                                :class="[notification.readAt ? '' : notification.color]"
                                >
                                    {{ notification.title }}
                                </span>
                                <span v-tooltip="notification.createdData" class="text-muted float-right font-size-sm">{{ notification.createdTime }}</span>
                            </div>

                            <span
                                class="text-muted"
                                :class="{'cursor-help text-underline-dotted': notification.errorsTooltip}"
                                :title="notification.errorsTooltip"
                            >
                                <template v-if="notification.descriptionLink">{{ notification.descriptionBefore }}<a :href="notification.fileUrl" target="_blank">{{ notification.descriptionLink }}</a>{{ notification.descriptionAfter }}</template>
                                <template v-else>{{ notification.description }}</template>
                            </span>
                        </div>
                    </li>
                </ul>
                <div v-else>{{ translate('notifications.messages_missing') }}</div>
            </div>
            <div class="dropdown-content-footer bg-light">
                <a @click="fetchNotification(notifications.length + showMoreCount)" class="text-grey mr-auto cursor-pointer">{{ translate('notifications.show_more') }}</a>
                <div>
                    <a @click="markRead" class="text-grey cursor-pointer" v-tooltip="'Позначити всі як прочитані'"><i
                        :class="[unreadNotifications ? 'icon-radio-unchecked' : 'icon-radio-checked2']"
                    ></i></a>
                </div>
            </div>
        </div>
    </li>
</template>
<script>
import mixins from './../mixins'
import moment from "moment";
import { mapGetters } from 'vuex';
import { httpClient } from '../services/HttpClient';
import { privateChannel } from '../spa/services/echoService';

// Broadcast-only notifications: they are pushed over the websocket to drive live
// UI updates (tables, statuses) but are NOT persisted, so they never appear in
// /notifications. Keep them out of the bell — only real notifications belong there.
const BROADCAST_ONLY_TYPES = [
    "App\\Notifications\\Pim\\AiProcessTask\\AiProcessTaskStatusChangedNotification"
];

// The event `.notification()` listens for under the hood — needed to unbind it
// with `stopListening` (Echo has no dedicated "un-notification" helper).
const BROADCAST_NOTIFICATION_EVENT = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

export default {
    name: 'NotificationComponent',
    mixins: [mixins],
    data() {
        return {
            show: false,
            notifications : [],
            showMoreCount : 5,
            subscribed: false,
            channel: null
        }
    },
    methods: {
        fetchNotification(limit)
        {
            httpClient.get(`/notifications`, {
                limit: limit
            }).then((response) => {
                this.updateNotifications(response.data.data)
            });
        },
        markRead()
        {
            if (this.notifications.length == 0) {
                return;
            }

            httpClient.post(`/notifications/read`, {
                limit: this.notifications.length
            }).then((response) => {
                this.updateNotifications(response.data.data)
            });
        },
        updateNotifications(notifications)
        {
            this.notifications = notifications.map(function (notification) {
                return this.makeNotification(
                    notification.id,
                    notification.data.title,
                    notification.data.description,
                    notification.data.log_type,
                    notification.read_at,
                    notification.created_at,
                    notification.data.additional_data?.file_url ?? null,
                    notification.data.additional_data?.errors ?? null
                )
            }, this)
        },
        makeNotification(id, title, description, logType = "info", readAt = null, createdAt = null, fileUrl = null, errors = null)
        {
            createdAt = createdAt ? moment(createdAt) : moment()

            let color = "text-primary-800";

            switch (logType) {
                case "error":
                    color = "text-danger-800";
                    break;
                case "warning":
                    color = "text-warning-800";
                    break;
                case "success":
                    color = "text-success-800";
                    break;
            }

            // A [[...]] marker in the description marks the text that becomes a link
            // to the notification's file. Strip it for plain rendering.
            let descriptionBefore = null;
            let descriptionLink = null;
            let descriptionAfter = null;

            const linkMatch = typeof description === 'string'
                ? description.match(/^([\s\S]*?)\[\[([\s\S]+?)\]\]([\s\S]*)$/)
                : null;

            if (linkMatch) {
                description = linkMatch[1] + linkMatch[2] + linkMatch[3];

                if (fileUrl) {
                    descriptionBefore = linkMatch[1];
                    descriptionLink = linkMatch[2];
                    descriptionAfter = linkMatch[3];
                }
            }

            // When the notification carries a list of errors (e.g. failed import),
            // expose them as a native title tooltip (one error per line) so the
            // user can see exactly what went wrong by hovering the description.
            const errorsTooltip = Array.isArray(errors) && errors.length > 0
                ? errors.map(error => String(error)).join('\n')
                : null;

            return {
                id : id,
                title : title,
                description : description,
                descriptionBefore : descriptionBefore,
                descriptionLink : descriptionLink,
                descriptionAfter : descriptionAfter,
                readAt : readAt,
                logType : logType,
                color : color,
                fileUrl : fileUrl,
                errorsTooltip : errorsTooltip,
                createdData : createdAt.format('hh:mm'),
                createdTime : createdAt.format('hh:mm')
            }
        },
        onDocumentClick(event)
        {
            if (this.show && this.$refs.root && !this.$refs.root.contains(event.target)) {
                this.show = false;
            }
        },
        subscribe()
        {
            const projectId = this.project?.id;
            const userId = this.profile?.id;

            if (this.subscribed || !projectId || !userId) {
                return;
            }

            this.subscribed = true;

            this.channel = privateChannel(`projects.${projectId}.users.${userId}`);
            this.channel.notification(this.handleNotification);
        },
        handleNotification(newNotification)
        {
            // Only persisted notifications (the ones /notifications returns) belong
            // in the bell. Broadcast-only events (e.g. table updates) are skipped here.
            const isPersisted = !BROADCAST_ONLY_TYPES.includes(newNotification.type);
            const isNew = !this.notifications.some(notification => notification.id === newNotification.id);

            // Gate side effects (toast/confirm) on "new" too, not just the bell insert —
            // otherwise a redelivered/duplicate websocket event re-triggers the toast
            // even though the bell correctly stays deduped.
            if (isPersisted && isNew) {
                this.notifications.unshift(
                    this.makeNotification(
                        newNotification.id,
                        newNotification.title,
                        newNotification.description,
                        newNotification.log_type,
                        null,
                        null,
                        newNotification.additional_data?.file_url ?? null,
                        newNotification.additional_data?.errors ?? null
                    )
                );

                this.applyNotificationSideEffects(newNotification);
            }

            // Let interested pages (tables that refresh on these events) react too.
            this.emitter.emit('notification:received', newNotification);
        },
        applyNotificationSideEffects(newNotification)
        {
            switch (newNotification.type) {
                case "App\\Modules\\Integration\\Notifications\\SettingsChangedNotification":
                    this.messageNotificator().confirm(newNotification.description, () => {
                        httpClient.get(`/integrations/${newNotification.additional_data.integration_uuid}/export`).then(() => {
                            this.messageNotificator().success(translate('alerts.success'))
                        });
                    })
                    break;
                case "App\\Modules\\Customization\\Notifications\\SuccessReloadFilledPercentNotification":
                    this.messageNotificator().success(newNotification.description)
                    document.dispatchEvent(new Event('stop-language-reload-filled-precent'));
                    break;
                case "App\\Modules\\Customization\\Notifications\\ErrorReloadFilledPercentNotification":
                    this.messageNotificator().error(newNotification.description)
                    document.dispatchEvent(new Event('stop-language-reload-filled-precent'));
                    break;
                default:
                    // Any other real notification (e.g. export/import finished) — a
                    // blue toast with its title, in addition to the bell record.
                    this.messageNotificator().information(newNotification.title)
                    break;
            }
        },
    },
    computed: {
        ...mapGetters({
            project: "route/project",
            profile: "profile/profile"
        }),
        unreadNotifications() {
            return this.notifications.filter(notification => notification.readAt === null).length > 0;
        },
        subscriptionKey() {
            return `${this.project?.id}:${this.profile?.id}`;
        }
    },
    watch: {
        subscriptionKey: {
            immediate: true,
            handler() {
                this.subscribe();
            }
        }
    },
    mounted() {
        this.fetchNotification(this.showMoreCount)
        document.addEventListener('click', this.onDocumentClick)
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onDocumentClick)

        // The underlying Echo connection outlives this component (it's an app-wide
        // singleton), so without this every remount (e.g. switching between layouts)
        // would stack another listener on the same channel — each firing its own
        // toast for the same notification.
        if (this.channel) {
            this.channel.stopListening(BROADCAST_NOTIFICATION_EVENT, this.handleNotification);
        }
    }
}
</script>
<style>
.dropdown-content-body {
    padding: 15px;
}
.dropdown-content-header:not([class*="bg-"]) + .dropdown-content-body {
    padding: 15px;
}
.cursor-pointer{
    cursor: pointer;
}
.text-underline-dotted{
    text-decoration: underline dotted;
}
.cursor-help{
    cursor: help;
}
</style>
